import React from 'react';
import cx from 'classnames';
import { SLDSButton } from '@salesforce/design-system-react';
import PropTypes from 'prop-types';

const {
  string,
  bool,
  oneOf
} = PropTypes;
function Button({
  className,
  full,
  inverse,
  size,
  variant,
  spinner,
  disabled,
  iconClassName,
  iconCategory,
  ...props
}) {
  const spinnerProps = spinner ? {
    iconCategory: "utility",
    iconName: "spinner",
    iconPosition: "left",
    iconClassName: cx("tds-button__spinner tds-button__icon", iconClassName)
  } : {};
  const iconClassNameProp = cx(iconClassName, {
    "tds-button__icon_custom": iconCategory && iconCategory !== "utility"
  });
  const TDSOnlyVariants = ["important", "tertiary"];

  // exclude iconVariant
  const {
    iconVariant,
    ...buttonProps
  } = props;
  let DSRButtonVariant;
  if (TDSOnlyVariants.includes(variant)) {
    DSRButtonVariant = "base";
  } else {
    DSRButtonVariant = variant;
  }
  return /*#__PURE__*/React.createElement(SLDSButton, Object.assign({
    className: cx(full && `tds-button_full`, size && `tds-button_${size}`, variant && !inverse && `tds-button_${variant}`, variant && inverse && `tds-button_${variant}-inverse`, className),
    iconCategory: iconCategory
  }, buttonProps, {
    variant: DSRButtonVariant || "base",
    iconClassName: iconClassNameProp,
    disabled: disabled || spinner
  }, spinnerProps));
}
Button.displayName = "Button";
/* The display name *has* to match the SLDSButton name in order for this
   component to be used as dropdown menu custom trigger, see:
   https://github.com/salesforce/design-system-react/blob/v0.8.22-es/components/menu-dropdown/button-trigger.jsx#L140
*/
Button.displayName = SLDSButton.displayName;
Button.defaultProps = {
  className: null,
  disabled: false,
  full: false,
  inverse: false,
  spinner: false,
  size: null,
  variant: null,
  iconClassName: null,
  iconCategory: null
};
Button.propTypes = {
  /**
   * CSS class name to be applied to `<button />` tag.
   */
  className: string,
  /**
   * Disables button, prevents clicks.
   */
  disabled: bool,
  /**
   * Makes button take the full width of its container.
   */
  full: bool,
  /**
   * Inverts colors, use a container with a dark background.
   */
  inverse: bool,
  /**
   * Displays spinning icon on the left side of the label.
   */
  spinner: bool,
  /**
   * Size variants.
   */
  size: oneOf(["small", "medium", "large", "xlarge", "x-large"]),
  /**
   * Button variants.
   */
  variant: oneOf(["base", "neutral", "brand", "destructive", "success", "danger", "icon", "tertiary", "important"]),
  /**
   * CSS class name to be applied to the icon.
   */
  iconClassName: string,
  /**
   * SLDS icon category such as `utility` or `custom`. See [SLDS icon types](https://www.lightningdesignsystem.com/components/icons/#Types).
   */
  iconCategory: string
};

export { Button as default };
